/* Global search / command palette (W3-H) — the overlay surface.
 *
 * Opened by the topbar search trigger and the `/` shortcut (main.ts). A
 * command-palette-style overlay in the shell's design grammar (tokens, borders-
 * only depth) that searches across what the hub really has client-side today:
 *   - clients (the shared GET /clients roster),
 *   - your tasks (the cross-client GET /me/tasks projection), and
 *   - screens (rail destinations + every client's cockpit deep pages).
 * Results are grouped by kind, keyboard-navigable (↑/↓/enter/esc), and deep-link
 * on select. The ranking/index is the pure lib/searchIndex.ts (unit-tested);
 * this module owns only the DOM + interaction.
 *
 * HONEST SCOPE: there is no global full-content task endpoint (per-client FTS
 * exists at /clients/{slug}/tasks?q= but no bulk one), so the footer states the
 * true search scope — no fake full-text pretense.
 *
 * An imperative island (like ui/graph.ts / ui/runModal.ts): it renders the
 * static dialog shell ONCE with lit, then patches only the results list on each
 * keystroke so the input keeps focus + caret. It follows the views/wiki.ts lit
 * conventions for every template it does render.
 */
import { html, render, nothing, type TemplateResult } from 'lit';
import { onClientsLoaded, type RegisterClient } from '../views/register.ts';
import { fetchMyWork, type MyWorkRow } from '../lib/myWork.ts';
import {
  buildIndex, search, flatten,
  type IndexClient, type IndexTask, type SearchEntry, type SearchGroup,
} from '../lib/searchIndex.ts';

const SCOPE_FOOT =
  'Searching clients · your tasks · screens — full-content search across all clients lands with the FTS service.';

/* ── shared data (fed once, reused every open — no per-open roster refetch) ── */

let clients: RegisterClient[] = [];
onClientsLoaded((rows) => {
  clients = rows;
  if (isOpen()) renderList(); // upgrade an open palette when the roster lands
});

let tasks: MyWorkRow[] = [];
let tasksLoaded = false;
let tasksInFlight: Promise<void> | null = null;
/** Load the cross-client task set once (the AM's /me/tasks). Cheap + session-
 * scoped; a failure is swallowed so search still works over clients + screens. */
function ensureTasks(): void {
  if (tasksLoaded || tasksInFlight) return;
  tasksInFlight = fetchMyWork()
    .then((r) => { tasks = r.tasks; tasksLoaded = true; if (isOpen()) renderList(); })
    .catch(() => { tasksLoaded = true; }) // honest: search proceeds without tasks
    .finally(() => { tasksInFlight = null; });
}

function currentIndex(): SearchEntry[] {
  const ic: IndexClient[] = clients.map((c) => ({
    slug: c.slug, brand: c.brand, descriptor: c.descriptor, domain: c.domain, lastRunFile: c.lastRunFile,
  }));
  const it: IndexTask[] = tasks.map((t) => ({
    task_id: t.task_id, client_slug: t.client_slug, brand: t.brand, title: t.title, status: t.status,
  }));
  return buildIndex(ic, it);
}

/* ── palette state ──────────────────────────────────────────────────────── */

let scrim: HTMLDivElement | null = null;
let query = '';
let selected = 0;
let flat: SearchEntry[] = [];

function isOpen(): boolean {
  return scrim !== null;
}

/** A small tokens-only glyph per result kind (no letters-as-logos). */
function kindGlyph(kind: SearchEntry['kind']): TemplateResult {
  if (kind === 'client') {
    return html`<svg viewBox="0 0 16 16" aria-hidden="true"><rect x="2.5" y="2.5" width="11" height="11" rx="2.5" fill="none" stroke="currentColor" stroke-width="1.3"></rect><circle cx="8" cy="8" r="2.1" fill="currentColor"></circle></svg>`;
  }
  if (kind === 'task') {
    return html`<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3.5 8.4l2.7 2.7 6-6.3" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg>`;
  }
  return html`<svg viewBox="0 0 16 16" aria-hidden="true"><rect x="2.3" y="3" width="11.4" height="8.4" rx="1.4" fill="none" stroke="currentColor" stroke-width="1.3"></rect><line x1="2.3" y1="6" x2="13.7" y2="6" stroke="currentColor" stroke-width="1.3"></line></svg>`;
}

/* ── rendering ──────────────────────────────────────────────────────────── */

function itemRow(entry: SearchEntry, idx: number): TemplateResult {
  const on = idx === selected;
  return html`<div
    class=${`cmdk-item${on ? ' on' : ''}`}
    role="option"
    id=${`cmdk-opt-${idx}`}
    aria-selected=${on ? 'true' : 'false'}
    data-idx=${idx}
    @mousemove=${() => { if (selected !== idx) { selected = idx; renderList(); } }}
    @click=${() => activate(entry)}>
    <span class="cmdk-glyph" aria-hidden="true">${kindGlyph(entry.kind)}</span>
    <span class="cmdk-text"><strong>${entry.title}</strong><small>${entry.subtitle}</small></span>
  </div>`;
}

function groupBlock(group: SearchGroup, offset: number): TemplateResult {
  return html`<div class="cmdk-group" role="group" aria-label=${group.label}>
    <div class="cmdk-group-label">${group.label}</div>
    ${group.entries.map((e, i) => itemRow(e, offset + i))}
  </div>`;
}

function listTemplate(groups: SearchGroup[]): TemplateResult {
  if (!groups.length) {
    return html`<div class="cmdk-empty">No matches${query ? html` for “${query}”` : nothing}. Try a client, a task, or a screen name.</div>`;
  }
  let offset = 0;
  const blocks: TemplateResult[] = [];
  for (const g of groups) { blocks.push(groupBlock(g, offset)); offset += g.entries.length; }
  return html`${blocks}`;
}

function renderList(): void {
  if (!scrim) return;
  const groups = search(currentIndex(), query);
  flat = flatten(groups);
  if (selected >= flat.length) selected = Math.max(0, flat.length - 1);
  const list = scrim.querySelector('#cmdkList') as HTMLElement | null;
  if (list) render(listTemplate(groups), list);
  const input = scrim.querySelector('.cmdk-input') as HTMLInputElement | null;
  if (input) input.setAttribute('aria-activedescendant', flat.length ? `cmdk-opt-${selected}` : '');
  // keep the active option in view on keyboard movement
  scrim.querySelector('.cmdk-item.on')?.scrollIntoView({ block: 'nearest' });
}

function shellTemplate(): TemplateResult {
  return html`<div class="cmdk" role="dialog" aria-modal="true" aria-label="Search Five Blocks">
    <div class="cmdk-input-row">
      <span class="cmdk-search-icon" aria-hidden="true">⌕</span>
      <input class="cmdk-input" type="text" role="combobox" aria-expanded="true"
        aria-controls="cmdkList" aria-autocomplete="list" spellcheck="false"
        placeholder="Search clients, your tasks, screens…"
        @input=${onInput} @keydown=${onKeydown}>
      <kbd>esc</kbd>
    </div>
    <div class="cmdk-list" id="cmdkList" role="listbox" aria-label="Results"></div>
    <div class="cmdk-foot">${SCOPE_FOOT}</div>
  </div>`;
}

/* ── interaction ────────────────────────────────────────────────────────── */

function onInput(ev: Event): void {
  query = (ev.target as HTMLInputElement).value;
  selected = 0;
  renderList();
}

function onKeydown(ev: KeyboardEvent): void {
  if (ev.key === 'ArrowDown') {
    ev.preventDefault();
    if (flat.length) { selected = (selected + 1) % flat.length; renderList(); }
  } else if (ev.key === 'ArrowUp') {
    ev.preventDefault();
    if (flat.length) { selected = (selected - 1 + flat.length) % flat.length; renderList(); }
  } else if (ev.key === 'Enter') {
    ev.preventDefault();
    const entry = flat[selected];
    if (entry) activate(entry);
  } else if (ev.key === 'Escape') {
    ev.preventDefault();
    closePalette();
  }
}

function activate(entry: SearchEntry): void {
  closePalette();
  if (entry.navKind === 'assign') location.assign(entry.href);
  else location.hash = entry.href;
}

/* ── open / close ───────────────────────────────────────────────────────── */

export function openPalette(): void {
  if (isOpen()) { (scrim!.querySelector('.cmdk-input') as HTMLInputElement | null)?.focus(); return; }
  ensureTasks();
  query = '';
  selected = 0;
  scrim = document.createElement('div');
  scrim.className = 'cmdk-scrim';
  scrim.addEventListener('mousedown', (e) => { if (e.target === scrim) closePalette(); });
  render(shellTemplate(), scrim);
  document.body.appendChild(scrim);
  document.body.classList.add('cmdk-open');
  renderList();
  (scrim.querySelector('.cmdk-input') as HTMLInputElement | null)?.focus();
}

export function closePalette(): void {
  if (!scrim) return;
  scrim.remove();
  scrim = null;
  document.body.classList.remove('cmdk-open');
}

/** Wire the topbar trigger + `/` shortcut to the palette. Idempotent-safe to
 * call once at boot. The `/` handler ignores keystrokes while the user is
 * already typing in a field (so "/" keeps its literal meaning there) and any
 * modifier combo. */
export function initCommandPalette(): void {
  document.getElementById('searchTrigger')?.addEventListener('click', () => openPalette());
  document.addEventListener('keydown', (ev) => {
    if (ev.key !== '/' || ev.ctrlKey || ev.metaKey || ev.altKey) return;
    if (isOpen()) return;
    const el = document.activeElement as HTMLElement | null;
    const tag = el?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el?.isContentEditable) return;
    ev.preventDefault();
    openPalette();
  });
}
